import type { ModuleNavLeaf } from "../../routes/moduleNavigation";
import { navHrefBasePath } from "../../routes/moduleNavigation.utils";
import { routePaths } from "../../routes/navigation";
import {
    onlineCockpitRouteAccessCodes,
    retailCockpitRouteAccessCodes,
} from "./cockpitTabPermissions";
import { hasAnyPermission } from "./pathAccess";
import {
    B2B_CLIENTS_PREFIX,
    B2B_ORDERS_PREFIX,
    B2B_SUPPLIERS_PREFIX,
    CAMPAIGNS_PREFIX,
    CAMPAIGNS_TABS,
    CLIENTS_TABS,
    ORDERS_TABS,
    RETAIL_CLIENTS_PREFIX,
    SUPPLIERS_TABS,
    WAREHOUSE_ORDERS_PREFIX,
    screenRouteAccessCodes,
} from "./screenTabPermissions";
import {
    frequencyRouteAccessCodes,
    loyaltyRouteAccessCodes,
} from "./routeAccessCodes";
import type { User } from "./types";

/**
 * Кодове за видимост в менюто по leaf id — без споделени legacy
 * (`clients.view`, `orders.view`, …), за да не „изтича“ Едро от Магазини.
 * `null` = съобщения / без матрица → path check.
 */
function menuAccessCodesForLeaf(leafId: string): string[] | null {
    switch (leafId) {
        case "global-admin":
            return ["users.manage", "roles.manage"];
        case "retail-cockpit":
            return retailCockpitRouteAccessCodes();
        case "retail-clients-frequency":
            return frequencyRouteAccessCodes();
        case "retail-percent-targets":
            return ["retail.percent_targets.view", "retail.percent_targets.manage"];
        case "retail-defects-sliven":
            return ["retail.defects_sliven.view", "retail.defects_sliven.manage"];
        case "retail-loyalty":
            return loyaltyRouteAccessCodes();
        case "retail-clients-registry":
            return screenRouteAccessCodes(RETAIL_CLIENTS_PREFIX, CLIENTS_TABS);
        case "wholesale-clients":
            return screenRouteAccessCodes(B2B_CLIENTS_PREFIX, CLIENTS_TABS);
        case "wholesale-b2b":
            return ["b2b.view", "b2b.manage"];
        case "wholesale-suppliers":
            return screenRouteAccessCodes(B2B_SUPPLIERS_PREFIX, SUPPLIERS_TABS);
        case "wholesale-promotions":
            return ["b2b.promotions.view", "b2b.promotions.manage"];
        case "wholesale-orders":
            return screenRouteAccessCodes(B2B_ORDERS_PREFIX, ORDERS_TABS);
        case "wholesale-traders-analytics":
            return ["bi.traders.view", "bi.traders.manage"];
        case "wholesale-campaigns":
            return screenRouteAccessCodes(CAMPAIGNS_PREFIX, CAMPAIGNS_TABS);
        case "online-cockpit":
            return onlineCockpitRouteAccessCodes();
        case "warehouse-dashboard":
            return ["warehouse.dashboard.view", "warehouse.dashboard.manage"];
        case "warehouse-workspace":
            return ["warehouse.workspace.view", "warehouse.workspace.manage"];
        case "warehouse-orders":
        case "warehouse-items":
            return screenRouteAccessCodes(WAREHOUSE_ORDERS_PREFIX, ORDERS_TABS);
        case "expenses-transport":
            return ["expenses.transport.view", "expenses.transport.manage"];
        case "expenses-marketing":
            return ["expenses.marketing.view", "expenses.marketing.manage"];
        case "expenses-salaries":
            return ["expenses.salaries.view", "expenses.salaries.manage"];
        case "global-messages":
        case "retail-messages":
        case "wholesale-messages":
        case "online-messages":
        case "warehouse-messages":
            return null;
        default:
            return null;
    }
}

function isMessagesNavHref(href: string) {
    return navHrefBasePath(href) === routePaths.messages;
}

/** Видимост на листо в сайдбар менюто (по-строго от canAccessPath за споделени URL). */
export function canAccessNavLeafForMenu(user: User | null, leaf: ModuleNavLeaf): boolean {
    if (!user?.isActive || !leaf.href) {
        return false;
    }

    if (isMessagesNavHref(leaf.href)) {
        return true;
    }

    const codes = menuAccessCodesForLeaf(leaf.id);

    if (codes !== null) {
        return hasAnyPermission(user, codes);
    }

    return false;
}
